/**
 * MONSTER TOWN — VENUE FIXTURE ART. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * The art builders behind the placeables declared in `src/content/venue.ts`.
 * They live here so the content file stays a readable list of DATA and this
 * file stays under the 500-line house rule.
 *
 * Every builder obeys the `ArtBuilder` contract: origin at the footprint centre,
 * floor at y = 0, no baked position/rotation, deterministic, kit materials only.
 */

import * as THREE from 'three'
import type { ArtContext } from '../core/placeable'
import type { PaletteName } from '../core/palette'
import { ball, boxGeo, boxOn, cone, cylinder, glassy, group, inked } from '../render/kit'
// The doorway mat is authored ONCE, by the interiors crew, and used by every
// scene that has a way out. See `exitDoorArt` below for why this import exists.
import { artExitMat } from '../interior/decorArt'
import { coinArt, servingArt } from './art'
import type { VenueItem } from './types'

// ---------------------------------------------------------------------------
// town-side: the shopfronts you walk into
// ---------------------------------------------------------------------------

/** A 3x2 exterior shopfront: walls, striped awning, big glass, a fat door. */
export function shopFrontArt(
  ctx: ArtContext,
  o: { wall: PaletteName; roof: PaletteName; stripe: PaletteName; sign: PaletteName },
): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('shopFront')
  const w = 3 * t
  const d = 2 * t

  const shell = boxOn(w * 0.94, t * 1.5, d * 0.86, { color: o.wall, name: 'shell' })
  g.add(inked(shell))
  // roof
  const roof = boxOn(w, t * 0.34, d * 0.96, { color: o.roof, y: t * 1.5, name: 'roof' })
  g.add(inked(roof))
  g.add(boxOn(w * 0.72, t * 0.24, d * 0.7, { color: o.roof, y: t * 1.84 }))

  // striped awning across the front (z = +d/2)
  const front = d * 0.44
  for (let i = 0; i < 6; i++) {
    g.add(
      boxOn(w / 6.4, t * 0.12, t * 0.5, {
        color: i % 2 === 0 ? o.stripe : 'flowerWhite',
        x: (i - 2.5) * (w / 6.2),
        y: t * 1.1,
        z: front + t * 0.16,
      }),
    )
  }
  // window + door
  g.add(boxOn(w * 0.42, t * 0.62, t * 0.08, { color: 'glass', x: -w * 0.24, y: t * 0.42, z: front }))
  const door = boxOn(t * 0.66, t * 1.0, t * 0.12, {
    color: 'doorWood',
    x: w * 0.26,
    z: front,
    name: 'door',
  })
  g.add(inked(door))
  g.add(ball(t * 0.07, { color: 'uiCoin', x: w * 0.26 + t * 0.22, y: t * 0.5, z: front + t * 0.09 }))
  // sign board over the door
  const sign = boxOn(w * 0.5, t * 0.34, t * 0.1, { color: o.sign, y: t * 1.24, z: front + t * 0.02, name: 'sign' })
  g.add(inked(sign))
  return g
}

/** The ice-cream shopfront gets a giant cone on the roof. Unmistakable. */
export function iceCreamFrontArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = shopFrontArt(ctx, { wall: 'wallCream', roof: 'roofTeal', stripe: 'iceStrawberry', sign: 'uiPrimary' })
  const big = group('bigCone')
  big.position.set(0, t * 2.08, 0)
  const c = cone(t * 0.3, t * 0.7, { color: 'cone', seg: 10 })
  c.rotation.x = Math.PI
  c.position.y = t * 0.35
  big.add(inked(c))
  big.add(ball(t * 0.26, { color: 'iceStrawberry', y: t * 0.86, seg: 10 }))
  big.add(ball(t * 0.22, { color: 'iceVanilla', y: t * 1.2, seg: 10 }))
  big.add(ball(t * 0.09, { color: 'cherry', y: t * 1.44, seg: 8 }))
  g.add(big)
  return g
}

/** The town shop gets a coin over the door instead. */
export function coinShopFrontArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = shopFrontArt(ctx, { wall: 'wallMint', roof: 'roofMustard', stripe: 'uiAccent', sign: 'uiCoin' })
  const c = coinArt(t * 1.1)
  c.position.set(0, t * 2.3, 0)
  c.name = 'bigCoin'
  g.add(c)
  return g
}

/**
 * The way back out of an interior. A doormat you cannot miss — AND NOTHING THAT
 * STANDS UP.
 *
 * This used to carry a full-height door leaf 0.42 of a tile in front of the mat.
 * The venue's exit cell sits mid-floor (it is lined up with the town's front
 * door), and the shop's is dead centre of a 12x10 room, so what a kid actually
 * saw was a brown door slab standing in the middle of an open floor with no wall
 * anywhere near it — in two scenes, and at every camera rotation, because there
 * is no wall there to cull it with. Flat is the only shape that cannot do that.
 *
 * Matches `artExitMat` in the house so the way out is one learned symbol
 * everywhere in the game.
 */
export function exitDoorArt(ctx: ArtContext): THREE.Object3D {
  /*
   * A WAY OUT HAS TO STAND UP (venue fix, r3).
   *
   * The mat alone was the whole exit, and the round-3 critic put it plainly:
   * inside a venue there is no HUD control to leave, so the only way out is a
   * small flat framed picture lying on the floor by the wall — "it reads as a
   * floor picture next to a large orange rug that looks far more like a
   * doorway. A 3-year-old has no way to learn that the little picture on the
   * floor is the door."
   *
   * So the mat keeps its job (it is the same learned symbol as the house's, via
   * the interiors crew's one builder) and gets a DOORWAY built over it: two
   * chunky posts, a lintel, a warm panel of daylight between them and a fat
   * arrow bobbing through it. It is a gateway, not a door leaf — a gateway is
   * the one door-shape that still reads correctly standing in the open, which
   * matters because the wall behind it is culled at two of the four camera
   * snaps. Ink outlines on the silhouette only, so the whole thing is six
   * draw calls.
   */
  const t = ctx.grid.tileSize
  const g = group('venueExit')
  g.add(artExitMat(ctx))

  const frame = group('door')
  // Pushed to the WALL side of its own tile. `rot` aims the mat at its wall and
  // the mat's own body is pushed +Z to sit under the threshold, so +Z is
  // "outside" for every exit in the game — the gateway and the arrow both point
  // that way or they are directing a kid back into the room.
  frame.position.z = t * 0.3
  // Sized to fit INSIDE its own tile — `interiorFitReport` measures exactly
  // this, and a doorway that overhangs its cell would clip whatever a kid puts
  // beside it.
  const postH = t * 1.5
  const halfW = t * 0.4
  for (const s of [-1, 1]) {
    const post = boxOn(t * 0.16, postH, t * 0.16, { color: 'woodLight', x: s * halfW })
    frame.add(inked(post))
  }
  const lintel = boxOn(t * 0.98, t * 0.2, t * 0.22, { color: 'roofTeal', y: postH })
  frame.add(inked(lintel))
  // Daylight in the opening. Flat and unlit so it stays the brightest thing in
  // a shaded room — "outside is through here" with no words at all.
  const light = boxOn(t * 0.66, postH - t * 0.06, t * 0.05, { color: 'sunWarm', y: 0, name: 'glow', flat: true })
  frame.add(light)
  g.add(frame)

  // THE ARROW LIES ON THE FLOOR, pointing at the opening. Stood up and aimed
  // out of the room it is seen end-on under a fixed iso camera and reads as a
  // coloured lozenge; flat on the mat, its whole shape is facing the lens.
  const arrow = group('wayOut')
  arrow.position.set(0, t * 0.09, -t * 0.2)
  arrow.add(inked(boxOn(t * 0.17, t * 0.05, t * 0.34, { color: 'uiPrimary', z: -t * 0.17 })))
  const head = cone(t * 0.27, t * 0.3, { color: 'uiPrimary', seg: 4 })
  head.rotation.x = Math.PI / 2
  head.rotation.y = Math.PI / 4
  head.position.z = t * 0.15
  arrow.add(inked(head))
  g.add(arrow)
  return g
}

// ---------------------------------------------------------------------------
// stations
// ---------------------------------------------------------------------------

/** An ice-cream tub with a big domed scoop of its flavour and a scoop handle. */
export function tubArt(ctx: ArtContext, color: PaletteName): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('tub')
  const cabinet = boxOn(t * 0.84, t * 0.5, t * 0.84, { color: 'metal', name: 'cabinet' })
  g.add(inked(cabinet))
  g.add(boxOn(t * 0.88, t * 0.08, t * 0.88, { color: 'metalDark', y: t * 0.5 }))
  const tub = cylinder(t * 0.32, t * 0.22, { color: 'metalDark', y: t * 0.58, seg: 12 })
  g.add(tub)
  const dome = ball(t * 0.3, { color, y: t * 0.82, seg: 12 })
  dome.name = 'dome'
  g.add(dome)
  g.add(ball(t * 0.1, { color: 'flowerWhite', x: -t * 0.1, y: t * 0.96, z: t * 0.1, seg: 8 }))
  // the scoop, on a stick, leaning in the tub — this is the "lever" we animate
  const lever = group('lever')
  lever.position.set(t * 0.24, t * 0.82, 0)
  lever.add(cylinder(t * 0.03, t * 0.34, { color: 'woodLight' }))
  lever.add(ball(t * 0.1, { color: 'metal', y: t * 0.36, seg: 8 }))
  lever.rotation.z = -0.35
  g.add(lever)
  return g
}

/** A stack of cones (or cups) waiting to be picked up. */
export function stackArt(ctx: ArtContext, kind: 'cone' | 'cup'): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group(`${kind}Stack`)
  const post = boxOn(t * 0.7, t * 0.42, t * 0.7, { color: 'woodLight', name: 'post' })
  g.add(inked(post))
  const holder = group('holder')
  holder.position.y = t * 0.42
  for (let i = 0; i < 3; i++) {
    if (kind === 'cone') {
      const c = cone(t * 0.16, t * 0.34, { color: 'cone', seg: 9 })
      c.rotation.x = Math.PI
      c.position.set(0, t * (0.17 + i * 0.14), 0)
      holder.add(c)
    } else {
      holder.add(cylinder(t * 0.16, t * 0.26, { color: 'plate', y: t * (i * 0.11), seg: 10 }))
    }
  }
  g.add(holder)
  return g
}

/** A topping jar. The contents are drawn from the item's own colour + shape. */
export function toppingJarArt(ctx: ArtContext, item: VenueItem): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('toppingJar')
  const base = boxOn(t * 0.62, t * 0.34, t * 0.62, { color: 'woodLight', name: 'base' })
  g.add(inked(base))
  const jar = cylinder(t * 0.24, t * 0.42, { color: 'glass', y: t * 0.34, seg: 12, name: 'jar' })
  g.add(jar)
  const fill = cylinder(t * 0.2, t * 0.26, { color: item.color, y: t * 0.36, seg: 12, name: 'fill' })
  g.add(fill)
  const lid = cylinder(t * 0.26, t * 0.08, { color: 'uiPrimary', y: t * 0.76, seg: 12, name: 'lid' })
  g.add(lid)
  g.add(ball(t * 0.07, { color: item.color, y: t * 0.9, seg: 8 }))
  return g
}

/** The serving counter. Tall, chunky, with a till and a little bell. */
export function counterArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('counter')
  const body = boxOn(t * 1.9, t * 0.72, t * 0.84, { color: 'woodLight', name: 'body' })
  g.add(inked(body))
  const top = boxOn(t * 2.0, t * 0.1, t * 0.94, { color: 'floorTile', y: t * 0.72, name: 'top' })
  g.add(top)
  g.add(boxOn(t * 1.7, t * 0.1, t * 0.06, { color: 'uiPrimary', y: t * 0.3, z: t * 0.44 }))
  // till
  const till = boxOn(t * 0.5, t * 0.3, t * 0.4, { color: 'metal', x: t * 0.6, y: t * 0.82, name: 'till' })
  g.add(till)
  g.add(boxOn(t * 0.34, t * 0.16, t * 0.05, { color: 'glassDark', x: t * 0.6, y: t * 1.0, z: t * 0.2 }))
  // bell
  const bell = ball(t * 0.13, { color: 'uiCoin', x: -t * 0.62, y: t * 0.9, seg: 10 })
  bell.name = 'bell'
  g.add(bell)
  g.add(cylinder(t * 0.16, t * 0.04, { color: 'uiCoinDark', x: -t * 0.62, y: t * 0.82, seg: 10 }))
  return g
}

/** A chilled display case so the shop looks like a shop even when empty. */
export function displayCaseArt(ctx: ArtContext, items: readonly VenueItem[]): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('displayCase')
  const body = boxOn(t * 1.86, t * 0.6, t * 0.8, { color: 'metal', name: 'body' })
  g.add(inked(body))
  // NOTE: build the glass by hand with the kit's shared `glassy` material —
  // never mutate a material returned by `toon()`, it is cached and shared.
  const glassBox = new THREE.Mesh(boxGeo(t * 1.8, t * 0.5, t * 0.74), glassy('glass', 0.45))
  glassBox.position.y = t * 0.6 + t * 0.25
  glassBox.name = 'glass'
  g.add(glassBox)
  const scoops = items.filter((i) => i.kind === 'scoop').slice(0, 4)
  for (let i = 0; i < scoops.length; i++) {
    const it = scoops[i]
    if (it === undefined) continue
    const x = (i - (scoops.length - 1) / 2) * t * 0.42
    g.add(cylinder(t * 0.15, t * 0.12, { color: 'metalDark', x, y: t * 0.6, seg: 10 }))
    g.add(ball(t * 0.14, { color: it.color, x, y: t * 0.78, seg: 10 }))
  }
  g.add(boxOn(t * 1.86, t * 0.08, t * 0.8, { color: 'metalDark', y: t * 1.1 }))
  return g
}

/** A menu board: pictures of every flavour, no words. LAW #4. */
export function menuBoardArt(ctx: ArtContext, items: readonly VenueItem[]): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('menuBoard')
  const post = cylinder(t * 0.07, t * 0.9, { color: 'woodDark' })
  g.add(post)
  const board = boxOn(t * 0.9, t * 0.8, t * 0.1, { color: 'wallCream', y: t * 0.9, name: 'board' })
  g.add(inked(board))
  const scoops = items.filter((i) => i.kind === 'scoop').slice(0, 4)
  for (let i = 0; i < scoops.length; i++) {
    const it = scoops[i]
    if (it === undefined) continue
    const col = i % 2
    const row = Math.floor(i / 2)
    g.add(
      ball(t * 0.12, {
        color: it.color,
        x: (col - 0.5) * t * 0.4,
        y: t * (1.45 - row * 0.36),
        z: t * 0.07,
        seg: 9,
      }),
    )
  }
  return g
}

/**
 * SEATS HAVE BACKS NOW, AND THAT IS THE WHOLE FIX (venue fix, r3).
 *
 * Every seat in the shop was a bare post with a disc on top. Sit a monster on
 * one and you get a body balanced on a stick: our round-bodied shapes are a
 * 1.0-wide ball with 0.1-long legs, so once the hips are on the seat there is
 * nothing at all below the belly to see. The round-3 critic read exactly that
 * and called it "a round head with two arm blobs sitting directly on a bare
 * brown post. No torso, no legs" — and they were right about what the picture
 * showed, whatever the rig was doing.
 *
 * There is no leg-length fix for a creature that has no legs. What says SITTING
 * is the FURNITURE: a cushioned seat wide enough for the body, a back rising
 * behind it, and arms either side. A monster inside that frame is unmistakably
 * sitting in a chair no matter what shape it is.
 *
 * `SEAT_TOP` is the one number the seat art and `customers.ts` share; the
 * customers' `STOOL_SEAT_Y`/`TABLE_SEAT_Y` are set from it.
 */
export const SEAT_TOP = 0.42
/**
 * Cushion radius. Big enough that the widest torso in `SHAPES` (`round`, 1.0
 * across) sits IN it rather than on it, small enough that two chairs and a table
 * still fit a 2x1 footprint without spilling into the tiles either side —
 * `interiorFitReport` measures exactly that.
 */
const SEAT_R = 0.38

/**
 * One chair. `back` is the local -Z side, which is where a customer's back goes:
 * a seated customer's yaw is `sitFacing`, and it faces local +Z.
 */
function chairArt(t: number, name: string, o: { seat: PaletteName; frame: PaletteName }): THREE.Group {
  const g = group(name)
  const seatTop = t * SEAT_TOP
  // legs first, so the ink outlines below sit on top of them
  for (const [dx, dz] of [
    [-0.26, -0.26],
    [0.26, -0.26],
    [-0.26, 0.26],
    [0.26, 0.26],
  ] as [number, number][]) {
    g.add(cylinder(t * 0.055, seatTop, { color: o.frame, x: dx * t, z: dz * t, seg: 6 }))
  }
  // the seat: a fat cushion on a board, so it has a lip a body can rest on
  const board = cylinder(t * SEAT_R, t * 0.08, { color: o.frame, y: seatTop - t * 0.08, seg: 14, name: 'seatBoard' })
  g.add(inked(board))
  const pad = cylinder(t * (SEAT_R - 0.02), t * 0.11, { color: o.seat, y: seatTop - t * 0.09, seg: 14, name: 'seat' })
  g.add(pad)

  // THE BACK. Two uprights and a fat top rail with a cushion panel between —
  // this is the part that reads as "chair" from across the room at iso.
  const backZ = -t * (SEAT_R - 0.06)
  const backH = t * 0.62
  for (const s of [-1, 1]) {
    g.add(cylinder(t * 0.05, backH, { color: o.frame, x: s * t * 0.3, y: seatTop, z: backZ, seg: 6 }))
  }
  const rail = boxOn(t * 0.74, t * 0.13, t * 0.11, { color: o.frame, y: seatTop + backH - t * 0.13, z: backZ })
  g.add(inked(rail))
  g.add(boxOn(t * 0.56, t * 0.3, t * 0.07, { color: o.seat, y: seatTop + t * 0.2, z: backZ }))

  // Little arms either side: they frame the body and stop a round monster
  // reading as a ball that happens to be near a chair. Two boxes, no posts —
  // three seats live in one small room and every part here is one a kid can
  // see the difference from across it.
  for (const s of [-1, 1]) {
    g.add(boxOn(t * 0.09, t * 0.07, t * 0.54, { color: o.frame, x: s * t * 0.32, y: seatTop + t * 0.19, z: -t * 0.06 }))
  }
  return g
}

/**
 * Table for two, with the chairs built in — one customer per table.
 *
 * TOP RADIUS AND CHAIR OFFSET ARE A PAIR, and they are set against the width of
 * a seated monster, not by eye. A monster sitting down puts its chest at exactly
 * tabletop height, so a chair tucked under the overhang does not look cosy — the
 * tabletop passes straight through the customer. `1.02 - 0.42` leaves clear air
 * between the widest torso in `SHAPES` and the edge of the top.
 * `TABLE_SEAT_X` in `customers.ts` is the same 1.02.
 */
const TABLE_TOP_R = 0.36
export const TABLE_SEAT_OFFSET = 0.92

export function tableArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('venueTable')
  const top = cylinder(t * TABLE_TOP_R, t * 0.12, { color: 'woodLight', y: t * 0.66, seg: 14, name: 'top' })
  g.add(inked(top))
  g.add(cylinder(t * 0.11, t * 0.66, { color: 'woodDark', seg: 10 }))
  g.add(cylinder(t * 0.32, t * 0.08, { color: 'woodDark', seg: 12 }))
  // Chairs face the table: the seat's local +Z is where the customer looks, and
  // `customers.ts` turns a table seat a quarter turn toward the top, so a chair
  // on the -X side is rotated +90 degrees about Y.
  for (const s of [-1, 1]) {
    const c = chairArt(t, s < 0 ? 'seatL' : 'seatR', { seat: 'fabric', frame: 'woodDark' })
    c.position.set(s * t * TABLE_SEAT_OFFSET, 0, 0)
    c.rotation.y = s < 0 ? Math.PI / 2 : -Math.PI / 2
    g.add(c)
  }
  // a tiny vase so an empty table still looks cared for
  g.add(cylinder(t * 0.07, t * 0.14, { color: 'wallBlue', y: t * 0.78, seg: 8 }))
  g.add(ball(t * 0.08, { color: 'flowerPink', y: t * 0.96, seg: 8 }))
  return g
}

/** A single chair — a cheap extra seat, so a small shop can still get busy. */
export function stoolArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('venueStool')
  g.add(chairArt(t, 'chair', { seat: 'cushion', frame: 'woodDark' }))
  return g
}

/** The start-over bin. Tapping it dumps the tray with a happy poof. */
export function binArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('venueBin')
  const body = cylinder(t * 0.32, t * 0.6, { color: 'uiAccent', seg: 12, name: 'body' })
  g.add(inked(body))
  const lid = cylinder(t * 0.36, t * 0.1, { color: 'uiAccentDark', y: t * 0.6, seg: 12, name: 'lid' })
  g.add(lid)
  g.add(ball(t * 0.08, { color: 'uiPanel', y: t * 0.72, seg: 8 }))
  return g
}

// ---------------------------------------------------------------------------
// town props: the job board and the coins you find in the grass
// ---------------------------------------------------------------------------

export function jobBoardArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('jobBoard')
  g.add(cylinder(t * 0.09, t * 0.7, { color: 'woodDark', x: -t * 0.3 }))
  g.add(cylinder(t * 0.09, t * 0.7, { color: 'woodDark', x: t * 0.3 }))
  const board = boxOn(t * 1.0, t * 0.72, t * 0.12, { color: 'woodLight', y: t * 0.7, name: 'board' })
  g.add(inked(board))
  // three pinned "jobs", drawn as coloured notes with a coin on each
  for (let i = 0; i < 3; i++) {
    const x = (i - 1) * t * 0.3
    g.add(boxOn(t * 0.24, t * 0.3, t * 0.03, { color: 'flowerWhite', x, y: t * 0.9, z: t * 0.07 }))
    const c = cylinder(t * 0.07, t * 0.03, { color: 'uiCoin', x, y: t * 1.02, z: t * 0.1, seg: 10 })
    c.rotation.x = Math.PI / 2
    g.add(c)
  }
  const roof = boxOn(t * 1.16, t * 0.1, t * 0.3, { color: 'roofRed', y: t * 1.42, name: 'roof' })
  g.add(inked(roof))
  return g
}

export function findCoinArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('findCoin')
  const c = coinArt(t * 0.55)
  c.position.y = t * 0.34
  c.rotation.y = ctx.variety * Math.PI
  c.name = 'spinner'
  g.add(c)
  // a few sparkle nubs so it catches the eye in long grass
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + ctx.variety
    g.add(
      ball(t * 0.05, {
        color: 'flowerWhite',
        x: Math.cos(a) * t * 0.34,
        y: t * 0.34 + Math.sin(a * 2) * t * 0.12,
        z: Math.sin(a) * t * 0.34,
        seg: 6,
      }),
    )
  }
  return g
}

/** A single serving on a pedestal — used by the venue's "today's special". */
export function specialArt(ctx: ArtContext, items: readonly VenueItem[]): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('special')
  const post = cylinder(t * 0.26, t * 0.66, { color: 'wallLilac', seg: 12, name: 'post' })
  g.add(inked(post))
  g.add(cylinder(t * 0.34, t * 0.08, { color: 'uiPrimary', y: t * 0.66, seg: 12 }))
  const scoops = items.filter((i) => i.kind === 'scoop')
  const base = items.find((i) => i.kind === 'base')
  const pick = (n: number): string | undefined => scoops[n % Math.max(1, scoops.length)]?.id
  const serving = servingArt(
    {
      base: base?.id ?? null,
      scoops: [pick(Math.floor(ctx.variety * 4)) ?? '', pick(Math.floor(ctx.variety * 4) + 1) ?? ''].filter(
        (x) => x !== '',
      ),
      topping: items.find((i) => i.kind === 'topping')?.id ?? null,
    },
    items,
    t,
  )
  serving.position.y = t * 0.74
  serving.name = 'serving'
  g.add(serving)
  return g
}
